import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { addDays } from 'date-fns';
import ukMoment from '../../../utils/ukTime';
import Select from 'react-select';

import Data from '../../../repository';
import Loading from "../../components/Loading";

import styles from '../../../styles/Admin.module.scss';

const createBookingSchema = yup.object({
	user: yup.object().nullable().required('Please select a user'),
	studio: yup.string().required('Please select a studio'),
	session: yup.string().required('Please select a session')
});

function Create() {
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [serverError, setServerError] = useState(null);
	const [success, setSuccess] = useState(false);

	const [users, setUsers] = useState([]);
	const [studios, setStudios] = useState([]);
	const [sessions, setSessions] = useState([]);
	const [loadingSessions, setLoadingSessions] = useState(false);

	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
		watch,
		setValue,
		reset
	} = useForm({
		resolver: yupResolver(createBookingSchema),
		defaultValues: {
			user: null,
			studio: '',
			session: ''
		}
	});

	const selectedStudio = watch('studio');

	useEffect(() => {
		fetchInitialData();
	}, []);

	const fetchInitialData = async () => {
		try {
			const [usersRes, studiosRes] = await Promise.all([
				Data.fetchUsers(),
				Data.fetchStudios()
			]);
			setUsers(usersRes.data);
			setStudios(studiosRes.data);
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setLoading(false);
		}
	};

	const updateStudio = async (studioId) => {
		setValue('session', '');
		setSessions([]);

		if (!studioId) return;

		setLoadingSessions(true);
		try {
			const studio = await Data.fetchSessions(studioId, new Date(), addDays(new Date(), 40));
			setSessions(studio.data);
		} catch (error) {
			console.error('Error fetching sessions:', error);
		} finally {
			setLoadingSessions(false);
		}
	};

	const onSubmit = async (data) => {
		const fullSession = sessions.find(s => s.start === data.session);

		if (!fullSession) {
			setServerError('Please select a valid session');
			return;
		}

		setSubmitting(true);
		setServerError(null);

		try {
			await Data.createUserBooking({
				user: data.user.value,
				session: fullSession
			});
			setSuccess(true);
		} catch (error) {
			setServerError(error.response?.data?.message || 'Failed to create booking. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	const handleCreateAnother = () => {
		setSuccess(false);
		setServerError(null);
		reset({ user: null, studio: '', session: '' });
		setSessions([]);
	};

	if (loading) return <Loading button={false} />;

	if (success) {
		return (
			<div className={styles.form}>
				<p style={{ color: 'green', gridColumn: '1 / -1' }}>Booking created successfully.</p>
				<div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
					<button className="btn btnPrimary" onClick={handleCreateAnother}>
						Create Another Booking
					</button>
					<Link to="/admin/bookings" className="btn btnEmpty">
						View All Bookings
					</Link>
				</div>
			</div>
		);
	}

	const userOptions = users.map(user => ({
		value: user._id,
		label: `${user.firstName} ${user.lastName} (${user.email})`
	}));

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
			<div className={styles.select}>
				<p>User: *</p>
				<Controller
					name="user"
					control={control}
					render={({ field }) => (
						<Select
							{...field}
							options={userOptions}
							placeholder="Select user..."
							isClearable
						/>
					)}
				/>
				{errors.user && <span className="form-field-error">{errors.user.message}</span>}
			</div>

			<div>
				<p>Studio: *</p>
				<select
					{...register('studio')}
					onChange={(e) => {
						register('studio').onChange(e);
						updateStudio(e.target.value);
					}}
					className={errors.studio ? 'input-error' : ''}
				>
					<option value="">Select Studio</option>
					{studios.map((studio) => (
						<option key={studio._id} value={studio._id}>{studio.name}</option>
					))}
				</select>
				{errors.studio && <span className="form-field-error">{errors.studio.message}</span>}
			</div>

			<div>
				<p>Session: *</p>
				<select
					{...register('session')}
					disabled={!selectedStudio || loadingSessions}
					className={errors.session ? 'input-error' : ''}
				>
					{loadingSessions ? (
						<option value="" disabled>Loading...</option>
					) : (
						<>
							<option value="">Select Session</option>
							{sessions.map((session) => (
								<option
									key={session.start}
									disabled={session.booked || session.past || session.withheld}
									value={session.start}
								>
									{ukMoment(session.start).format('ddd Do MMM YYYY - HH:mm')}
								</option>
							))}
						</>
					)}
				</select>
				{errors.session && <span className="form-field-error">{errors.session.message}</span>}
			</div>

			{serverError && <p style={{ color: 'red' }}>{serverError}</p>}

			<button type="submit" className="btn btnPrimary" disabled={submitting}>
				{submitting ?
					<Loading button={true} />
					:
					`Create Booking`
				}
			</button>
		</form>
	);
}

export default Create;
